import { z } from 'zod';
import { evaluateExpression, validateCSVFormat, parseCSVLine } from '../shared';
import { validateRebarFormat } from '../shared/utils/inputUtils';

/**
 * Base schema for individual field validation
 * Keep ALL input values as strings (since inputs emit strings),
 * and let Zod coerce/validate.
 */
export const baseSchema = z.object({
  project: z.string().min(1, 'Project is required'),
  title: z.string().min(1, 'Title is required'),

  length: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n > 0.0;
  }, 'Length must be a valid expression greater than 0'),

  tributaryWidth: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n > 0.0;
  }, 'Tributary Width must be a valid expression greater than 0'),

  addedWeight: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0.0;
  }, 'Added Weight must be a valid expression greater than or equal to 0 and 1000 psf'),

  // Dropdown fields
  supports: z.coerce
    .number()
    .int()
    .refine(n => [1, 2, 3, 4, 5, 6].includes(n), 'Supports must be between 1 and 6'),

  orientation: z.coerce
    .number()
    .int()
    .refine(n => [0, 1].includes(n), 'Orientation must be 1 (Horizontal) or 0 (Vertical)'),

  sectionType: z.coerce
    .number()
    .int()
    .refine(n => [0, 1, 2].includes(n), 'Section Type must be 0 (Rectangular) or 1 (Circular) or 2 (T)'),

  b: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n > 0.0;
  }, 'b must be a valid expression greater than 0'),

  h: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n > 0.0;
  }, 'h must be a valid expression greater than 0'),

  dia: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n > 0.0;
  }, 'dia must be a valid expression greater than 0'),

  tf: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0.0;
  }, 'tf must be a valid expression greater than or equal to 0'),

  tw: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0.0;
  }, 'tw must be a valid expression greater than or equal to 0'),

  rebar: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  rebarDepth: z.string().superRefine((val, ctx) => {
    const err = validateRebarFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  cover: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0.0;
  }, 'Cover must be a valid expression greater than or equal to 0'),

  shearRebar: z.string().regex(/^\d-n\d@\d+$/, {
    message: 'Expected format like 2-n4@4'
  }),

  fy: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 1 && n <= 100000;
  }, 'Fy must be a valid expression between 1 and 100000'),

  fc: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 1 && n <= 10000;
  }, 'Fc must be a valid expression between 1 and 10000'),

  dif: z.coerce
    .number()
    .int()
    .refine(n => [0, 1, 2].includes(n), 'DIF must be 0 (Far Range), 1 (Close In), or 2 (Ones)'),

  axialLoad: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Axial Load must be a valid expression greater than or equal to 0'),

  pressureHistory: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  timeHistory: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  u0: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null;
  }, 'u0 must be a valid expression greater than or equal to 0'),
  v0: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null;
  }, 'v0 must be a valid expression greater than or equal to 0')
});

/**
 * Full schema with cross-field validation
 */
export const schema = baseSchema.superRefine((data, ctx) => {
  if (parseCSVLine(data.pressureHistory)?.length !== parseCSVLine(data.timeHistory)?.length) {
    const msg = 'Pressure and time histories must have the same number of values.';
    ctx.addIssue({ code: 'custom', message: msg, path: ['pressureHistory'] });
    ctx.addIssue({ code: 'custom', message: msg, path: ['timeHistory'] });
  }

  if (parseCSVLine(data.rebar)?.length !== parseCSVLine(data.rebarDepth)?.length) {
    const msg = 'Rebar and rebar diameter entries must have the same number of values.';
    ctx.addIssue({ code: 'custom', message: msg, path: ['rebar'] });
    ctx.addIssue({ code: 'custom', message: msg, path: ['rebarDiameter'] });
  }
});

/**
 * Helper types
 */
export type FieldName = keyof z.infer<typeof baseSchema>;
export type FormValues = z.input<typeof schema>; // raw form shape (strings from inputs/selects)
export type ParsedValues = z.output<typeof schema>; // parsed shape (numbers after coercion)

export type FormErrors = Partial<Record<FieldName, string | null>>;

/**
 * Validate a single field
 */
export function validateOneField<K extends FieldName>(key: K, raw: unknown): string | null {
  const fieldSchema = baseSchema.shape[key];
  const res = fieldSchema.safeParse(raw);
  return res.success ? null : (res.error.issues[0]?.message ?? 'Invalid value');
}
